import { pluralized } from "common/utils/strings";

import Results from "client/common/components/results/Results";
import { ProductListSource } from "client/analytics/types";
import { ProductListContext } from "client/analytics/utils";

import { useFilterPanel } from "../hooks";
import { useProductFilterDefinitions } from "../hooks/filterDefinitions";
import { ProductSearch, ProductSearchFilters, SelectedProductFilters } from "../types";
import FilterLayout from "./FilterLayout";
import FilterPanel from "./FilterPanel";
import ProductCardList from "./ProductCardList";
import ProductCardListHeader from "./ProductCardListHeader";

type ProductCardResultsProps = {
  products: ProductSearch;
  isRefetching: boolean;
  filters: SelectedProductFilters;
  onFiltersChange: (next: Partial<SelectedProductFilters>) => void;
  onPageChange: (page: number) => void;
  listSource: ProductListSource;
  listContext?: ProductListContext;
  /** Pluralized in the count and the empty state, e.g. "deal" reads "Showing 1 - 14 of 14 deals". */
  entityName: string;
  /** Overrides for the empty state, which otherwise reads off `entityName`. */
  empty?: {
    header?: string;
    description?: string;
    /** Used instead of `description` when the shopper has a filter applied. */
    filteredDescription?: string;
  };
  /** Whether the page offers filtering; false hides the rail, the filter button and the applied row, leaving the count alone. */
  showFilters?: boolean;
  /** The column the header and the cards render in. */
  className?: string;
  /** Filters the page shows outside the rail, e.g. as pills. */
  keysHiddenFromPanel?: string[];
};

/**
 * A page of product cards with its filter rail, count header, applied filters and pagination.
 * Callers fetch the products and own the loading and error states.
 */
const ProductCardResults = ({
  products,
  isRefetching,
  filters,
  onFiltersChange,
  onPageChange,
  listSource,
  listContext,
  entityName,
  empty,
  showFilters = true,
  className,
  keysHiddenFromPanel = [],
}: ProductCardResultsProps) => {
  const definitions = useProductFilterDefinitions().map((definition) =>
    keysHiddenFromPanel.includes(definition.key)
      ? { ...definition, isHiddenFromPanel: true }
      : definition,
  );
  const panel = useFilterPanel<SelectedProductFilters, ProductSearchFilters>({
    definitions,
    response: products,
    filters,
    onChange: onFiltersChange,
    isEnabled: showFilters,
    listSource,
    listContext,
  });
  const { filterOptions, showFilterPanel, hasSelectedFilters } = panel;

  return (
    <FilterLayout panel={showFilterPanel && <FilterPanel.Rail {...panel} />}>
      <div className={className}>
        <ProductCardListHeader
          definitions={definitions}
          paginator={products.paginator}
          isRefetching={isRefetching}
          entityName={entityName}
          filterOptions={filterOptions}
          filters={filters}
          changeFrom={panel.changeFrom}
          showFilters={showFilters && !showFilterPanel}
        />
        <FilterPanel.AppliedRow {...panel} />
        {products.results.length ? (
          <ProductCardList
            results={products.results}
            listSource={listSource}
            listContext={{ ...listContext, numResults: products.paginator.count }}
            paginator={products.paginator}
            onPageChange={onPageChange}
            entityName={entityName}
            isNarrow={showFilterPanel}
          />
        ) : (
          <Results.None
            header={empty?.header ?? `No ${pluralized(0, entityName)} found`}
            description={
              hasSelectedFilters
                ? (empty?.filteredDescription ??
                  `No ${pluralized(0, entityName)} match the filters you have applied.`)
                : empty?.description
            }
          />
        )}
      </div>
    </FilterLayout>
  );
};

export default ProductCardResults;
